import { PlaygroundHistoryPanel } from "./playground-history-panel";
import { usePlaygroundPresentation } from "./playground-presentation";
// holds conversations
import { useThemeMode } from "@/contexts/ThemeContext";
import { ArrowLeft, ArrowRight, LockKeyhole, Plus } from "lucide-react";
import { PlaygroundConversations } from "./playground-conversations";
import { cn } from "@/utils/cn";
import { useQueryState } from "nuqs";
import { useSidebarStore } from "@/atoms/playground";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import {
  CONFIDENTIAL_ROUTE_ID,
  useConfidentialSessionStore,
} from "@/atoms/confidential-session";
import { useEffect, useId, useState } from "react";
import { useHistorySidebarAccessibility } from "./history-sidebar-accessibility";
import { getConfidentialTimeLeft } from "@/utils/confidential-mode";
import { usePlaygroundRouting } from "../routing/playground-routing";

export const PlaygroundSidebar = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [search, setSearch] = useQueryState("q", { defaultValue: "" });
  const sidebarState = useSidebarStore();
  const { modern } = usePlaygroundPresentation();
  const accessibility = useHistorySidebarAccessibility();
  const localId = useId();
  const historyId = accessibility?.id ?? localId;
  const { id, navigateToNewChat } = usePlaygroundRouting();
  const storedTheme = useStoredTheme();
  const { mode } = useThemeMode();
  const confidentialSession = useConfidentialSessionStore(
    (state) => state.session,
  );
  const [countdownNow, setCountdownNow] = useState(Date.now());
  const isConfidentialRoute = id === CONFIDENTIAL_ROUTE_ID;

  useEffect(() => {
    if (!isConfidentialRoute || !confidentialSession) return;

    setCountdownNow(Date.now());
    const interval = window.setInterval(
      () => setCountdownNow(Date.now()),
      60_000,
    );
    return () => window.clearInterval(interval);
  }, [confidentialSession, isConfidentialRoute]);

  const handleNewChat = () => {
    navigateToNewChat();
  };

  const newChatButton = (
    <button
      type="button"
      aria-label={t("playground.startNewChat")}
      onClick={handleNewChat}
      className="flex h-[34px] w-[48px] cursor-pointer items-center justify-center rounded-xl bg-[var(--bg)] hover:bg-[var(--hover-bg)]"
      style={
        {
          "--bg":
            storedTheme.button.primary.backgroundColor[mode],
          "--hover-bg":
            storedTheme.button.primary.hoverBackgroundColor[mode],
        } as React.CSSProperties
      }
    >
      <Plus
        color={storedTheme.button.primary.textColor[mode]}
      />
    </button>
  );

  const historyContent = isConfidentialRoute ? (
    confidentialSession?.title ? (
      <div className="flex flex-col gap-2 py-4">
        <span className="px-2 py-1 text-[14.8px] font-bold leading-[22.2px] text-foreground opacity-70">
          {t("conversation.today")}
        </span>
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1">
          <LockKeyhole className="size-4 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-1 whitespace-nowrap text-xs font-normal tracking-[0.18px]">
            <div className="truncate leading-[1.5] text-foreground">
              {confidentialSession.title}
            </div>
            <div className="truncate leading-[1.5] text-[var(--warning)]">
              {t("playground.confidentialShortLabel")}
              <span aria-hidden="true"> • </span>
              {t("playground.timeLeft", {
                time: getConfidentialTimeLeft(confidentialSession.expiresAt, countdownNow),
              })}
            </div>
          </div>
        </div>
      </div>
    ) : null
  ) : (
    <PlaygroundConversations search={search} />
  );

  if (modern) {
    return (
      <PlaygroundHistoryPanel
        search={search}
        onSearchChange={value => { void setSearch(value || null); }}
        onNewChat={handleNewChat}
      >
        {historyContent}
      </PlaygroundHistoryPanel>
    );
  }

  return (
    <div
      id={historyId}
      ref={accessibility?.panel}
      aria-hidden={!sidebarState.isOpen}
      {...(!sidebarState.isOpen ? { inert: "" } : {})}
      className={cn(
        "transition-all duration-300 motion-reduce:transition-none max-h-screen flex flex-col shrink-0",
        {
          "w-[220px] sm:w-[260px] md:w-[280px] p-2": sidebarState.isOpen,
          "w-0 p-0 overflow-hidden": !sidebarState.isOpen,
        },
      )}
    >
      <div
        hidden={!sidebarState.isOpen}
        style={{
          padding: "16px 0",
        }}
        className={cn("justify-between items-center", sidebarState.isOpen ? "flex" : "hidden")}
      >
        <h2 className="text-[16px] font-bold">
          {t("conversation.conversations")}
        </h2>
        <div className="flex items-center gap-2">
          {newChatButton}
          <button
            ref={accessibility?.closer}
            aria-expanded={sidebarState.isOpen}
            aria-controls={historyId}
            aria-label={
              sidebarState.isOpen
                ? t("playground.closeSidebar")
                : t("playground.openSidebar")
            }
            onClick={sidebarState.toggle}
            type="button"
            className="w-[34px] h-[34px] flex items-center justify-center p-1 cursor-pointer rounded-full bg-[var(--bg)] hover:bg-[var(--hover-bg)]"
            style={
              {
                "--bg":
                  storedTheme.button.primary.backgroundColor[
                    mode
                  ],
                "--hover-bg":
                  storedTheme.button.primary.hoverBackgroundColor[
                    mode
                  ],
              } as React.CSSProperties
            }
          >
            {isRTL ? (
              <ArrowRight size={20} color={storedTheme.button.primary.textColor[mode]} />
            ) : (
              <ArrowLeft size={20} color={storedTheme.button.primary.textColor[mode]} />
            )}
          </button>
        </div>
      </div>
      {sidebarState.isOpen && (
        <>
          <div
            style={{
              scrollbarColor: "var(--muted-foreground) transparent",
            }}
            className="flex-1 overflow-y-auto pr-1 rtl:pr-0 rtl:pl-1"
          >
            {historyContent}
          </div>
        </>
      )}
    </div>
  );
};
